import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Gift, Star, Calendar, CreditCard, Shield, ChevronRight, 
  Sparkles, Package, ShoppingBag, Code, TrendingUp, BookOpen, Video,
  Award, Users, Smartphone, Monitor, FileText, Headphones, ShoppingCart,
  Shirt, Coffee, Package2, Palette, Clock, Zap, BarChart, Download,
  Globe, DollarSign, MessageSquare, Youtube, Mail, Image, Timer,
  TreePine, Heart, Ghost, Gem, AlertCircle, Play, TrendingDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../config/api';

const UpgradeNicheDominator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes countdown
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(false);
  const [spotsLeft, setSpotsLeft] = useState(47); // Starting spots

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 180 && !showUrgencyBanner) { // Show urgency at 3 minutes
          setShowUrgencyBanner(true);
        }
        return prevTime > 0 ? prevTime - 1 : 0;
      });
    }, 1000);

    // Simulate spots being taken
    const spotsTimer = setInterval(() => {
      setSpotsLeft((prev) => {
        const decrement = Math.random() > 0.7 ? 1 : 0;
        return prev > 10 ? prev - decrement : prev;
      });
    }, 15000); // Every 15 seconds

    return () => {
      clearInterval(timer);
      clearInterval(spotsTimer);
    };
  }, [showUrgencyBanner]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpgrade = async () => {
    setIsProcessing(true);
    try {
      // TODO: Implement actual upgrade logic with payment processing
      console.log('Processing Niche Dominator Template Pack upgrade...');
      // Redirect to payment processor or handle upgrade
    } catch (error) {
      console.error('Upgrade error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNoThanks = () => {
    // Redirect to dashboard or next offer
    navigate('/dashboard');
  };

  return (
    <>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Kalam:wght@400;700&display=swap" rel="stylesheet" />
      
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide {
          from { transform: translateX(0); }
          to { transform: translateX(20px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
      
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Urgency Banner - Shows when time is low */}
      {showUrgencyBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-4 shadow-lg animate-pulse">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-4 text-sm md:text-base font-bold">
            <AlertCircle className="w-5 h-5" />
            <span>⚠️ WARNING: This offer expires in {formatTime(timeLeft)} - Don't miss out!</span>
            <AlertCircle className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Scarcity Bar - Dark Mode Design */}
      <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white py-4 px-4 overflow-hidden shadow-2xl border-b border-gray-700">
        {/* Animated background effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 via-orange-600/30 to-red-600/20"></div>
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255, 255, 255, 0.03) 10px,
                rgba(255, 255, 255, 0.03) 20px
              )`,
              animation: 'slide 20s linear infinite'
            }}
          ></div>
        </div>
        
        {/* Enhanced flashing lights effect */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-2 left-10 w-3 h-3 bg-red-400 rounded-full animate-ping shadow-lg"></div>
          <div className="absolute top-3 right-20 w-2 h-2 bg-orange-400 rounded-full animate-ping animation-delay-500 shadow-lg"></div>
          <div className="absolute bottom-2 left-1/3 w-2 h-2 bg-yellow-400 rounded-full animate-ping animation-delay-1000 shadow-lg"></div>
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-red-500 rounded-full animate-pulse animation-delay-1500"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Spots Counter - Enhanced */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg blur opacity-75 animate-pulse"></div>
                <div className="relative flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 px-5 py-2.5 rounded-lg border border-yellow-400/50 shadow-lg">
                  <div className="flex items-center gap-1">
                    <TrendingDown className="w-5 h-5 text-yellow-300 animate-bounce" />
                    <AlertCircle className="w-4 h-4 text-yellow-300 animate-pulse" />
                  </div>
                  <span className="font-black text-lg text-white drop-shadow-lg">
                    ONLY {spotsLeft} SPOTS LEFT
                  </span>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 text-sm text-yellow-200 font-medium">
                <Sparkles className="w-4 h-4" />
                <span>at this special price</span>
              </div>
            </div>

            {/* Progress Bar - Enhanced */}
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <div className="bg-black/50 rounded-full h-4 overflow-hidden shadow-inner">
                  <div 
                    className="relative h-full transition-all duration-500 ease-out rounded-full"
                    style={{ 
                      width: `${((100 - spotsLeft) / 100) * 100}%`,
                      background: `linear-gradient(to right, #10b981 0%, #eab308 ${((100 - spotsLeft) / 100) * 50}%, #ef4444 100%)`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/30"></div>
                    <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/50 animate-pulse"></div>
                  </div>
                </div>
                <p className="text-xs text-yellow-200 mt-1 text-center font-medium">
                  🔥 {100 - spotsLeft} marketers already claimed their spot!
                </p>
              </div>
            </div>

            {/* Timer - Enhanced */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg blur opacity-75 animate-pulse"></div>
              <div className="relative flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-orange-600 px-5 py-2.5 rounded-lg border border-yellow-300/50 shadow-lg">
                <Clock className="w-5 h-5 text-white animate-spin-slow drop-shadow" />
                <div>
                  <p className="text-xs text-yellow-100 font-medium">EXPIRES IN</p>
                  <span className="font-black text-xl text-white tabular-nums drop-shadow-lg">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section - Enhanced with Video */}
      <div className="bg-gradient-to-br from-orange-600 via-red-600 to-pink-600 text-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl"></div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 py-12 relative z-10">
          <div className="text-center mb-8">
            {/* Enhanced badge - centered */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 backdrop-blur-md px-8 py-4 rounded-full mb-8 animate-bounce border border-yellow-400/30 mx-auto">
              <Gift className="w-6 h-6 text-yellow-300" />
              <span className="font-bold text-lg uppercase tracking-wide">WAIT! One Last Thing...</span>
              <Gift className="w-6 h-6 text-yellow-300" />
            </div>
            
            {/* Enhanced headline - centered */}
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight text-center">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-yellow-200 drop-shadow-2xl">
                You Made The Smart Choice.
              </span>
              <span className="block text-3xl md:text-5xl mt-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300">
                Now Let's Make It <span className="underline decoration-wavy decoration-4 underline-offset-8">Profitable, Faster.</span>
              </span>
            </h1>
          </div>

          {/* Video Placeholder - Now inside hero */}
          <div className="mb-8 animate-fadeIn max-w-4xl mx-auto">
            <div 
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl aspect-video flex items-center justify-center shadow-2xl relative overflow-hidden cursor-pointer hover:shadow-3xl transition-all duration-300 group border border-white/20"
            >
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-30">
                <div 
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255, 255, 255, 0.03) 35px, rgba(255, 255, 255, 0.03) 70px)`,
                  }}
                ></div>
              </div>
              
              {/* Play button */}
              <div className="text-center z-10 group-hover:scale-110 transition-transform duration-300">
                <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center mb-4 mx-auto shadow-2xl group-hover:shadow-3xl transition-shadow">
                  <Play className="w-14 h-14 text-purple-600 ml-2" />
                </div>
                <p className="text-white font-bold text-2xl mb-2 drop-shadow-lg">Unlock Your Template Empire</p>
                <p className="text-purple-100 text-lg drop-shadow">Watch this 2-minute message from our founder</p>
              </div>

              {/* Video duration badge */}
              <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur text-white px-4 py-2 rounded-lg font-bold">
                2:14
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Congratulations Section with Handwritten Font */}
        <div className="text-center mb-12">
          <div className="mb-8 relative">
            {/* Decorative sparkles */}
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
              <Sparkles className="w-10 h-10 text-yellow-500 animate-pulse" />
            </div>
            <div className="absolute top-0 left-10 md:left-20">
              <Star className="w-6 h-6 text-purple-400 animate-spin-slow" />
            </div>
            <div className="absolute top-10 right-10 md:right-20">
              <Sparkles className="w-6 h-6 text-pink-400 animate-pulse animation-delay-500" />
            </div>
            
            <h2 
              className="text-5xl md:text-7xl text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 mb-6 leading-tight animate-float"
              style={{ fontFamily: "'Caveat', cursive", fontWeight: 700 }}
            >
              Congratulations!
            </h2>
            
            <p 
              className="text-2xl md:text-3xl text-gray-700 mb-6 leading-relaxed"
              style={{ fontFamily: "'Kalam', cursive" }}
            >
              Upgrading to the Professional Version was the right move. 
              <br />
              <span className="text-purple-600 font-bold">You now have every advanced feature</span> and a massive bank of credits at your disposal.
            </p>
            
            <p className="text-xl text-gray-800 font-semibold mb-6 max-w-2xl mx-auto">
              You possess the tools. The question is, 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600"> what will you build with them?</span>
            </p>
          </div>
          {/* The Problem Card - Modern Professional Design */}
          <div className="relative mb-12 max-w-3xl mx-auto">
            {/* Main Card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Content */}
              <div className="p-10">
                {/* Quote Mark */}
                <div className="absolute top-8 left-8 text-8xl text-orange-200 font-serif leading-none">"</div>
                
                {/* Main Text */}
                <div className="relative z-10 space-y-6">
                  <p className="text-xl text-gray-700 leading-relaxed font-medium">
                    The biggest hurdle for any creator isn't the lack of a good tool...
                  </p>
                  
                  {/* Highlighted Problem */}
                  <div className="text-center py-8">
                    <div className="inline-block relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-400 transform rotate-1 rounded-lg"></div>
                      <div className="relative bg-white px-8 py-4 rounded-lg shadow-lg transform -rotate-1 transition-transform hover:rotate-0">
                        <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                          It's the "Blank Canvas Problem"
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Explanation */}
                  <div className="space-y-4">
                    <p className="text-gray-600 leading-relaxed text-lg">
                      That overwhelming moment when you stare at endless possibilities, unsure where to start.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
                      <div className="text-center p-4 bg-gray-50 rounded-xl">
                        <Clock className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-gray-700">Hours wasted</p>
                        <p className="text-xs text-gray-500">deciding what to create</p>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-xl">
                        <Zap className="w-8 h-8 text-red-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-gray-700">Creative paralysis</p>
                        <p className="text-xs text-gray-500">from too many options</p>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-xl">
                        <Users className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-gray-700">Generic results</p>
                        <p className="text-xs text-gray-500">that look like everyone else's</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Question */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <p className="text-center text-2xl font-bold text-gray-800">
                    What if you could skip straight to 
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600"> professional results?</span>
                  </p>
                </div>
              </div>
              
              {/* Bottom Gradient Fade */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></div>
            </div>
            
            {/* Shadow Effect */}
            <div className="absolute -bottom-4 left-8 right-8 h-20 bg-gradient-to-b from-gray-200/50 to-transparent rounded-3xl blur-xl"></div>
          </div>
        </div>

      </div>

      {/* Main Offer - Full Width Gradient */}
      <div className="relative py-20 mb-16 bg-gradient-to-br from-purple-700 via-pink-600 to-red-600 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        {/* Floating Decorative Elements */}
        <div className="absolute top-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float animation-delay-1000"></div>
        <div className="absolute top-1/2 right-1/3 w-28 h-28 bg-yellow-400/20 rounded-full blur-xl animate-pulse animation-delay-500"></div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Main Card */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-12 text-center">
            {/* Decorative Elements */}
            <div className="absolute top-6 left-6 w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-20"></div>
            <div className="absolute bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-red-400 to-orange-400 rounded-full opacity-20"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-800 mb-8 leading-tight">
                <span className="block">
                  Instantly Inject <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-red-600">250+ Niche-Specific </span>
                </span>
                <span className="block mt-2">
                  <span>Templates Directly Into Your Account</span>
                </span>
              </h2>
              
              <div className="max-w-4xl mx-auto space-y-8">
                <p className="text-xl text-gray-700 leading-relaxed">
                  This is the ultimate shortcut. We've had our team of professional designers and marketers analyze the most profitable online business models and create four exclusive "Niche Dominator" template packs.
                </p>
                
                {/* Strategic Assets Highlight */}
                <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-red-50 rounded-2xl p-8 border-2 border-purple-200">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <Sparkles className="w-8 h-8 text-purple-600" />
                    <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-red-600">
                      These aren't just more templates.
                    </p>
                    <Sparkles className="w-8 h-8 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-800">
                    These are <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-600">strategic assets.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">

        {/* Template Packs - New Professional Design */}
        <div className="mb-16">
          <div className="text-center mb-16">
            {/* Enhanced Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 px-6 py-3 rounded-full mb-8 border border-orange-200">
              <Package className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-bold text-orange-700 uppercase tracking-wider">Template Preview</span>
            </div>
            
            {/* Enhanced Headline */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-800 mb-6 leading-tight">
              Here's a Look Inside The 
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-red-600 to-pink-600">
                "Niche Dominator" Packs
              </span>
              <span className="block text-2xl md:text-3xl mt-4 text-gray-600 font-bold">
                You'll Unlock Today...
              </span>
            </h2>
            
            {/* Enhanced Description */}
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                This isn't just a random collection of images. We analyzed the most profitable online business models and designed four complete, strategic visual toolkits.
              </p>
              
              {/* Highlight Box */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border-2 border-orange-200 max-w-3xl mx-auto">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Star className="w-6 h-6 text-orange-600" />
                  <p className="text-lg font-bold text-gray-800">
                    Below is just a small sample of what you'll find inside each pack.
                  </p>
                  <Star className="w-6 h-6 text-orange-600" />
                </div>
                <p className="text-base text-gray-600">
                  Each template is professionally designed and ready to use immediately.
                </p>
              </div>
            </div>
          </div>
          
          {/* Course Creator Pack */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-orange-400 to-red-400 p-4 rounded-2xl shadow-lg">
                    <Star className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">The "Course Creator" Pack</h3>
                    <p className="text-gray-600">50+ Premium Templates for Info Products & Coaching</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-gray-700 mb-6 text-lg">
                  If you sell info products, courses, or coaching, this pack is engineered to make your knowledge look as valuable as it truly is. Build a premium brand and command higher prices with visuals that scream professionalism.
                </p>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <p className="font-semibold text-gray-800 mb-4">Here's a sneak peek of the templates inside:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Spiral-Bound Workbook on a Desk</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Testimonial Quotes for Social Media</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Stack of 3 Hardcover "Module" Books</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Private Community on a Phone Screen</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Monitor className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Course Curriculum on an iMac Display</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Video Lesson on a Student's iPad</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Award className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Official Certificate of Completion</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Lead Magnet Checklist Mockups</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Sales Page "Bundle Shot" (All Devices)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Headphones className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Podcast/Audio Lesson on a Phone</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-4 text-center font-semibold">
                  Perfect for: Course Creators • Coaches • Consultants • Authors • Speakers
                </div>
              </div>
            </div>
          </div>

          {/* E-commerce Pack */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-red-400 to-pink-400 p-4 rounded-2xl shadow-lg">
                    <ShoppingBag className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">The "E-commerce Pro" Pack</h3>
                    <p className="text-gray-600">50+ High-Converting Templates for Physical Products</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-gray-700 mb-6 text-lg">
                  If you sell physical products, this pack is your secret weapon for stopping the scroll and driving sales. Create stunning promotional graphics and lifestyle shots that make your products fly off the virtual shelves.
                </p>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <p className="font-semibold text-gray-800 mb-4">Here's a sneak peek of the templates inside:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Shirt className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">On-Model T-Shirt & Hoodie Shots</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ShoppingCart className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">"Buy 1, Get 1 Free" Ad Templates</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Package2 className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Skincare/Cosmetic Bottle Sets</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Customer Review Overlays</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Image className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Product Box & Contents (Flat Lay)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Animated "New Arrival" Posts</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Coffee className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Coffee Mug in a Cafe Setting</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Gem className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">"Limited Edition" Packaging Mockups</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Palette className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Multi-SKU Color Variation Showcase</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ShoppingBag className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">Tote Bag in a Lifestyle Environment</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl p-4 text-center font-semibold">
                  Perfect for: E-commerce Stores • Dropshippers • Print-on-Demand • Fashion Brands • Product Creators
                </div>
              </div>
            </div>
          </div>

          {/* SaaS Pack */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-orange-400 to-pink-400 p-4 rounded-2xl shadow-lg">
                    <Code className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">The "SaaS & Software" Pack</h3>
                    <p className="text-gray-600">50+ Professional Templates for Digital Products</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-gray-700 mb-6 text-lg">
                  If you sell software, apps, or digital tools, this pack will make you look like an industry leader. Clearly communicate the power and simplicity of your software with visuals designed for clarity and conversion.
                </p>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <p className="font-semibold text-gray-800 mb-4">Here's a sneak peek of the templates inside:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Monitor className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">SaaS Dashboard on an Ultrawide Monitor</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">"Book a Demo" CTA Graphics</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">App UI Flow Across Three Phones</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">Integration Logos Showcase (e.g., Slack)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">"New Feature" Announcement Graphics</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <BarChart className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">Case Study/Graph in a Browser</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">Classic Software Box for Digital Products</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">App Store & Google Play Screenshot Sets</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">Pricing Page on a Tablet Mockup</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">Ebook/Whitepaper Lead Magnets</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white rounded-2xl p-4 text-center font-semibold">
                  Perfect for: SaaS Founders • App Developers • Software Companies • Digital Tool Creators
                </div>
              </div>
            </div>
          </div>

          {/* Affiliate Pack */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-red-400 to-orange-400 p-4 rounded-2xl shadow-lg">
                    <TrendingUp className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">The "Affiliate & IM" Pack</h3>
                    <p className="text-gray-600">50+ Conversion-Focused Templates for Marketers</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-gray-700 mb-6 text-lg">
                  If you're a marketer who promotes products, this pack is built to maximize your click-through rates and commissions. Create high-value bonus pages and compelling promos that your audience can't ignore.
                </p>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <p className="font-semibold text-gray-800 mb-4">Here's a sneak peek of the templates inside:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Gift className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">"My Exclusive Bonuses" Stack Graphic</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">"Limited Time Discount" Vouchers</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">5-Star Product Review Templates</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Animated GIF Banner Ad Templates</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Youtube className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">YouTube Thumbnail for Video Reviews</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">"Here's What You Get" Value Stacks</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <BarChart className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Product Comparison Chart (A vs. B)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Email Header Graphics for Promos</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">"Problem vs. Solution" Infographics</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Testimonial Carousel Graphics</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-4 text-center font-semibold">
                  Perfect for: Affiliate Marketers • Product Reviewers • Email Marketers • Social Media Influencers
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Fast Action Section - True Full Width Gradient */}
      <div className="relative py-20 mb-16 bg-gradient-to-br from-orange-600 via-red-600 to-pink-600 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        {/* Floating Decorative Elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-float animation-delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-yellow-400/20 rounded-full blur-xl animate-pulse"></div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Main Card */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-12 text-center">
            {/* Decorative Quote */}
            <div className="absolute top-8 left-8 text-6xl text-orange-200 font-serif leading-none">"</div>
            <div className="absolute bottom-8 right-8 text-6xl text-orange-200 font-serif leading-none transform rotate-180">"</div>
            
            <div className="relative z-10">
              <h3 className="text-4xl md:text-5xl font-black text-gray-800 mb-8 leading-tight">
                This Is The Single Fastest Way To Turn Your New Tool Into A 
                <span className="block mt-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-red-600 to-pink-600">
                  Profit-Generating Machine
                </span>
              </h3>
              
              <div className="max-w-4xl mx-auto space-y-6">
                <p className="text-xl text-gray-700 leading-relaxed">
                  You can try to figure out the best visuals for your business through trial and error, or you can just let us give them to you.
                </p>
                
                {/* Highlighted Decision Point */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border-2 border-orange-200">
                  <p className="text-2xl font-bold text-gray-800 mb-4">
                    And to make this decision completely undeniable...
                  </p>
                  <p className="text-lg text-gray-700">
                    When you upgrade today, <span className="font-bold text-orange-600">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>, we're adding two more fast-action bonuses.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">

        {/* Bonuses Header */}
        <div className="text-center mb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-4">
              But Wait... There's <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Even More!</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              To make this offer completely irresistible, we're including these exclusive bonuses when you act today:
            </p>
          </div>
        </div>

        {/* Bonuses */}
        <div className="mb-12">
          {/* Holiday Marketing Pack - Full Design */}
          <div className="mb-8 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-orange-400 to-red-400 p-4 rounded-2xl shadow-lg">
                    <Calendar className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-orange-600 mb-1">FAST-ACTION BONUS #1</h3>
                    <h4 className="text-2xl font-bold text-gray-800">The Holiday Marketing Pack</h4>
                    <p className="text-gray-600">50+ Seasonal Templates for Year-Round Promotions</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-gray-700 mb-6 text-lg">
                  Never again scramble at the last minute for a promotional graphic during the most profitable times of the year. This powerful bonus pack gives you an entire calendar's worth of professionally designed, conversion-focused templates for the biggest sales events.
                </p>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <p className="font-semibold text-gray-800 mb-4">Here's a sneak peek of the templates inside:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-gray-700">Black Friday "Door Buster" Ad</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <TreePine className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Christmas "Gift Guide" Mockup</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">Cyber Monday "Tech Deal" Mockup</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-pink-500 flex-shrink-0" />
                      <span className="text-gray-700">New Year's "Fresh Start" Sale Graphic</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Ghost className="w-5 h-5 text-orange-600 flex-shrink-0" />
                      <span className="text-gray-700">Halloween "Spooky Sale" Social Post</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Heart className="w-5 h-5 text-pink-600 flex-shrink-0" />
                      <span className="text-gray-700">Valentine's Day "Couples Gift" Set</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Timer className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-gray-700">Animated "Countdown to Sale" Timer</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700">BFCM "Bundle Offer" Value Stack</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ShoppingCart className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-gray-700">"Last Chance for Shipping" Graphic</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Gift className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-gray-700">Christmas-Themed Product Scenes</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-4 text-center font-semibold">
                  Perfect for: Holiday Sales • Seasonal Promotions • Limited-Time Offers • Event Marketing
                </div>
              </div>
            </div>
          </div>

          {/* Credit Rebate Bonus */}
          <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-5xl mx-auto">
            {/* Top Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
            
            <div className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-gradient-to-br from-pink-400 to-orange-400 p-3 rounded-2xl shadow-lg">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-wide text-orange-600">FAST-ACTION BONUS #2</h4>
                  <h5 className="text-xl font-bold text-gray-800">Instant Credit Rebate (500 Credits)</h5>
                </div>
              </div>
              <p className="text-gray-600 text-lg">
                As a thank you for going all-in, we're immediately depositing an additional 500 credits into your account when you add this to your order. Use them to create even more amazing visuals with your new templates.
              </p>
            </div>
          </div>
        </div>

        {/* Value Stack - Enhanced Design */}
        <div className="relative mb-12 max-w-6xl mx-auto">
          <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Top Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
            
            <div className="p-12">
              {/* Header */}
              <div className="text-center mb-10">
                <h3 className="text-4xl font-bold text-gray-800 mb-4">
                  The Value Stack Is <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">INSANE.</span><br />
                  The Choice is <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-600">Obvious.</span>
                </h3>
                <p className="text-xl text-gray-600">
                  Let's summarize everything you're getting with this one-time-only upgrade:
                </p>
              </div>
            
              {/* Value Items */}
              <div className="max-w-3xl mx-auto mb-10">
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="space-y-4">
                    {/* Course Creator Pack */}
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-orange-400 to-red-400 p-2 rounded-lg">
                          <Star className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">The Course Creator Pack</span>
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400 line-through text-sm">$97</span>
                        <span className="text-green-600 font-bold text-xl ml-2">INCLUDED</span>
                      </div>
                    </div>

                    {/* E-commerce Pack */}
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-red-400 to-pink-400 p-2 rounded-lg">
                          <ShoppingBag className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">The E-commerce Pro Pack</span>
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400 line-through text-sm">$97</span>
                        <span className="text-green-600 font-bold text-xl ml-2">INCLUDED</span>
                      </div>
                    </div>

                    {/* SaaS Pack */}
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-orange-400 to-pink-400 p-2 rounded-lg">
                          <Code className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">The SaaS & Software Pack</span>
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400 line-through text-sm">$97</span>
                        <span className="text-green-600 font-bold text-xl ml-2">INCLUDED</span>
                      </div>
                    </div>

                    {/* Affiliate Pack */}
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-red-400 to-orange-400 p-2 rounded-lg">
                          <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">The Affiliate & IM Pack</span>
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400 line-through text-sm">$97</span>
                        <span className="text-green-600 font-bold text-xl ml-2">INCLUDED</span>
                      </div>
                    </div>

                    {/* Bonus Divider */}
                    <div className="flex items-center gap-4 py-2">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-orange-400 to-transparent"></div>
                      <span className="text-orange-600 font-bold uppercase tracking-wide px-4">Plus Today's Bonuses</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-orange-400 to-transparent"></div>
                    </div>

                    {/* Holiday Pack Bonus */}
                    <div className="flex justify-between items-center p-4 rounded-lg bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-orange-400 to-red-400 p-2 rounded-lg">
                          <Calendar className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">
                          <span className="text-orange-600 font-bold">BONUS:</span> The Holiday Marketing Pack
                        </span>
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400 line-through text-sm">$67</span>
                        <span className="text-orange-600 font-bold text-xl ml-2">FREE!</span>
                      </div>
                    </div>

                    {/* Credit Bonus */}
                    <div className="flex justify-between items-center p-4 rounded-lg bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-200">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-pink-400 to-orange-400 p-2 rounded-lg">
                          <CreditCard className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">
                          <span className="text-pink-600 font-bold">BONUS:</span> 500 Instant Credit Rebate
                        </span>
                      </span>
                      <div className="text-right">
                        <span className="text-gray-400 line-through text-sm">$90</span>
                        <span className="text-pink-600 font-bold text-xl ml-2">FREE!</span>
                      </div>
                    </div>

                    {/* Total Value */}
                    <div className="mt-6 pt-6 border-t-2 border-gray-300">
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-gray-800">Total Real-World Value:</span>
                        <div className="text-right">
                          <span className="text-4xl font-bold text-red-500 line-through">$545</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="text-center">
                <p className="text-xl text-gray-600 mb-4">
                  Normally, these packs will be sold individually for $97 each inside the member's area.
                </p>
                <p className="text-2xl text-gray-800 font-semibold mb-2">
                  But you won't pay <span className="text-red-500 line-through">$545</span>. You won't even pay <span className="text-red-500 line-through">$197</span>.
                </p>
                <p className="text-xl text-gray-600 mb-8">
                  On this page, right now, you can add this entire collection of 250+ niche templates and bonuses to your account for a single payment of just:
                </p>
                
                {/* Price Display */}
                <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 p-1 rounded-3xl">
                  <div className="bg-white rounded-3xl px-12 py-8 shadow-inner">
                    <div className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 text-8xl font-black">$97</div>
                    <p className="text-gray-600 text-lg mt-2 font-semibold">One-Time Payment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Shadow Effect */}
          <div className="absolute -bottom-4 left-8 right-8 h-20 bg-gradient-to-b from-gray-200/50 to-transparent rounded-3xl blur-xl"></div>
        </div>

        {/* CTA Section */}
        <div className="text-center mb-12">
          <button
            onClick={handleUpgrade}
            disabled={isProcessing}
            className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white text-xl font-bold py-6 px-12 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 mb-4 inline-flex items-center gap-3 relative group"
          >
            {/* Button pulse effect */}
            <span className="absolute -inset-1 bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 rounded-2xl opacity-75 blur-lg group-hover:opacity-100 transition-opacity animate-pulse"></span>
            
            <span className="relative flex items-center gap-3">
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6" />
                  YES! Add The Template Packs To My Order For Just $97
                </>
              )}
            </span>
          </button>
          
          {/* Countdown Timer Under CTA */}
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
              <Timer className="w-4 h-4 animate-pulse" />
              <span className="font-bold">Offer expires in: <span className="tabular-nums">{formatTime(timeLeft)}</span></span>
            </div>
          </div>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-gray-500" />
            <p className="text-sm text-gray-600">Your purchase is protected by our 30-Day Money-Back Guarantee</p>
          </div>

        </div>
      </div>

      </div>

      {/* Money-Back Guarantee Section - Full Width Gradient */}
      <div className="relative py-20 mb-16 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(135deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        {/* Floating Decorative Elements */}
        <div className="absolute top-10 left-10 w-36 h-36 bg-white/10 rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-10 right-10 w-44 h-44 bg-white/10 rounded-full blur-2xl animate-float animation-delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-yellow-400/20 rounded-full blur-xl animate-pulse animation-delay-500"></div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Main Card */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-12 text-center">
            {/* Decorative Shield Elements */}
            <div className="absolute top-6 left-6 w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full opacity-20"></div>
            <div className="absolute bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-full opacity-20"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mb-6 shadow-xl">
                  <Shield className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-4 leading-tight">
                  And Of Course, Your Upgrade is 
                  <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600">
                    Fully Protected
                  </span>
                </h2>
              </div>

              {/* Guarantee Content */}
              <div className="max-w-4xl mx-auto space-y-8">
                <p className="text-xl text-gray-700 leading-relaxed">
                  We're so confident that this template collection will be the ultimate shortcut to creating stunning, niche-specific visuals that we're protecting it with our
                </p>

                {/* Guarantee Badge */}
                <div className="py-8">
                  <div className="inline-block relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 transform rotate-1 rounded-2xl"></div>
                    <div className="relative bg-white px-10 py-8 rounded-2xl shadow-xl transform -rotate-1">
                      <p className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
                        30-Day, No-Questions-Asked
                      </p>
                      <p className="text-xl md:text-2xl font-bold text-green-600 mt-3">
                        Money-Back Guarantee
                      </p>
                    </div>
                  </div>
                </div>

                {/* Guarantee Details */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    Take the full 30 days to use these templates. If you don't find that they save you hours of creative guesswork and provide immense value to your business, just send us a single email and we will refund 100% of your purchase price for this upgrade.
                  </p>
                  
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800 mb-2">
                      You either <span className="text-green-600">love the assets</span>, or you get your <span className="text-green-600">money back</span>.
                    </p>
                    <p className="text-xl text-gray-600">
                      The risk is all on us.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">

      {/* Two Paths Closing Section */}
      <div className="relative mb-12 max-w-6xl mx-auto">
        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Top Accent Bar */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
          
          <div className="p-10">
            {/* Header */}
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                You're at a <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Crossroads</span>: 
                The Hard Way or The Shortcut.
              </h2>
            </div>

            {/* Two Paths */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              {/* Path 1 - The Hard Way */}
              <div className="relative">
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 h-full border-2 border-gray-300">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-3xl">😔</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">Path 1 (The Hard Way)</h3>
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed text-center">
                    You can skip this offer and leave with the powerful tools you've already purchased. You'll have the ability to create mockups, but you'll start from a blank canvas every time, trying to figure out what works for your business, and risking your visuals looking like thousands of others.
                  </p>
                  
                  <div className="mt-6 text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg">
                      <Clock className="w-4 h-4 mr-2" />
                      <span className="font-semibold">More Time & Guesswork</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Path 2 - The Shortcut */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl blur opacity-50"></div>
                <div className="relative bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 h-full border-2 border-orange-300">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-3xl">🚀</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Path 2 (The Shortcut)</h3>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed text-center">
                    For a single, small investment, you can let us do the hard work for you. You get a complete arsenal of 250+ professionally designed, niche-specific templates that are proven to convert, ensuring you always have the perfect visual ready to go in minutes.
                  </p>
                  
                  <div className="mt-6 text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg shadow-lg">
                      <Sparkles className="w-4 h-4 mr-2" />
                      <span className="font-semibold">Instant Success</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Choice Statement */}
            <div className="text-center mb-8">
              <p className="text-2xl font-bold text-gray-800 mb-6">
                The choice is yours.
              </p>
              <p className="text-lg text-gray-600">
                Click the button below to add the entire Template Collection to your order and become a true power user.
              </p>
            </div>

            {/* Final CTA */}
            <div className="text-center">
              <button
                onClick={handleUpgrade}
                disabled={isProcessing}
                className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white text-2xl font-bold py-8 px-16 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 mb-4 inline-flex items-center gap-4 relative group"
              >
                {/* Button pulse effect */}
                <span className="absolute -inset-2 bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 rounded-2xl opacity-75 blur-xl group-hover:opacity-100 transition-opacity animate-pulse"></span>
                
                <span className="relative flex items-center gap-4">
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-8 h-8" />
                      YES! Add The Complete Template Collection To My Order For Just $97
                    </>
                  )}
                </span>
              </button>
              
              <p className="text-sm text-gray-500 italic">
                (This Is a One-Time Offer. You Will Not See This Page Again)
              </p>
              
              {/* Countdown Timer Under Final CTA */}
              <div className="mt-4">
                <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-6 py-3 rounded-xl border border-red-200">
                  <Timer className="w-5 h-5 animate-pulse" />
                  <span className="font-bold text-lg">Final chance expires in: <span className="tabular-nums text-xl">{formatTime(timeLeft)}</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Shadow Effect */}
        <div className="absolute -bottom-4 left-8 right-8 h-20 bg-gradient-to-b from-gray-200/50 to-transparent rounded-3xl blur-xl"></div>
      </div>

        {/* No Thank You Link - Bottom of Page */}
        <div className="text-center mb-12">
          <button
            onClick={handleNoThanks}
            className="text-gray-500 hover:text-gray-700 underline text-sm max-w-4xl mx-auto leading-relaxed transition-colors duration-200"
          >
            No, thank you. I'll take my chances with the standard templates and create all my own niche-specific visuals from scratch. I understand I am forfeiting this entire collection of 250+ templates and bonuses forever and will have to pay the full price later if I change my mind.
          </button>
        </div>
      </div>

      {/* FOOTER - Professional Design */}
      <footer className="relative bg-gray-900 text-gray-300 mt-20">
        {/* Top border accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
        
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Logo and Company Info */}
          <div className="text-center mb-12">
            <img 
              src="/clawbrand_logo_color.png" 
              alt="ClawBrand AI" 
              className="h-16 mx-auto mb-6"
            />
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Transform your marketing with AI-powered mockups. Create stunning visuals in seconds, not hours.
            </p>
          </div>
            
          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12 text-sm">
            <a href="/support" className="hover:text-white transition-colors duration-200">Support</a>
            <span className="text-gray-600">•</span>
            <a href="/privacy" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
            <span className="text-gray-600">•</span>
            <a href="/refund" className="hover:text-white transition-colors duration-200">Refund Policy</a>
            <span className="text-gray-600">•</span>
            <a href="/cookies" className="hover:text-white transition-colors duration-200">Cookie Policy</a>
            <span className="text-gray-600">•</span>
            <a href="/terms" className="hover:text-white transition-colors duration-200">Terms of Service</a>
            <span className="text-gray-600">•</span>
            <a href="/disclaimer" className="hover:text-white transition-colors duration-200">Disclaimer</a>
          </div>
          
          {/* Disclaimers */}
          <div className="space-y-6 text-xs text-gray-500 max-w-4xl mx-auto">
            {/* Facebook Disclaimer */}
            <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
              <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">Facebook Disclaimer</h4>
              <p className="leading-relaxed">
                In no way are we associated with Facebook. Once you leave Facebook the responsibility is no longer on any Instagram owned entity.
                Your information and privacy is totally secure and will NEVER be shared. Facebook is in NO WAY associated with this website or anything here.
              </p>
            </div>
            
            {/* General Disclaimer */}
            <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
              <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">General Disclaimer</h4>
              <p className="leading-relaxed">
                Please note that this product does not provide any guarantee of income or success. The results achieved by the product owner or any other individuals mentioned are not indicative of future success or earnings. This website is not affiliated with FaceBook or any of its associated entities. Once you navigate away from FaceBook, the responsibility for the content and its usage lies solely with the user. All content on this website, including but not limited to text, images, and multimedia, is protected by copyright law and the Digital Millennium Copyright Act. Unauthorized copying, duplication, modification, or theft, whether intentional or unintentional, is strictly prohibited. Violators will be prosecuted to the fullest extent of the law. We want to clarify that JVZoo serves as the retailer for the products featured on this site. JVZoo® is a registered trademark of BBC Systems Inc., a Florida corporation located at 1809 E. Broadway Street, Suite 125, Oviedo, FL 32765, USA, and is used with permission. The role of JVZoo as a retailer does not constitute an endorsement, approval, or review of these products or any claims, statements, or opinions used in their promotion.
              </p>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="text-center mt-12 pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default UpgradeNicheDominator;
